import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api } from "@/lib/api";
import type { CalendarOccurrence, CalendarTask, DayPlan, Project, Task, TaskDetail } from "@/lib/types";
import { t } from "@shared/i18n";

export type TaskFilters = { view?: string; projectId?: string; q?: string; status?: string; sort?: string };

const qs = (o: Record<string, string | undefined>) => {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(o)) if (v) p.set(k, v);
  return p.toString();
};

export const useTasks = (f: TaskFilters) =>
  useQuery({ queryKey: ["tasks", "list", f], queryFn: async () => (await api<{ data: Task[] }>(`/api/tasks?${qs(f)}`)).data });

export const useTask = (id: string | null) =>
  useQuery({ queryKey: ["tasks", "detail", id], queryFn: async () => (await api<{ data: TaskDetail }>(`/api/tasks/${id}`)).data, enabled: Boolean(id) });

export type TaskStats = { open: number; overdue: number; dueToday: number; doneWeek: number; doneOnTimeWeek: number; plannedMinutesOpen: number; actualMinutesWeek: number; daily: { day: string; n: number }[] };
export const useTaskStats = () => useQuery({ queryKey: ["tasks", "stats"], queryFn: async () => (await api<{ data: TaskStats }>("/api/tasks/stats")).data });

export const useProjects = () => useQuery({ queryKey: ["projects"], queryFn: async () => (await api<{ data: Project[] }>("/api/projects")).data });

/** Invalide tout ce qui dépend des tâches/agenda (listes, stats, briefing, planning). */
export function useInvalidateWork() {
  const qc = useQueryClient();
  return () =>
    Promise.all(["tasks", "projects", "calendar", "planning", "briefing"].map((k) => qc.invalidateQueries({ queryKey: [k] })));
}

export function useTaskMutations() {
  const invalidate = useInvalidateWork();
  const onError = (e: Error) => toast.error(e.message);
  return {
    create: useMutation({ mutationFn: (body: Partial<Task> & { dependsOn?: string[] }) => api<{ data: Task }>("/api/tasks", { method: "POST", json: body }), onSuccess: invalidate, onError }),
    update: useMutation({
      mutationFn: ({ id, ...body }: Partial<Task> & { id: string; dependsOn?: string[] }) => api<{ data: Task }>(`/api/tasks/${id}`, { method: "PATCH", json: body }),
      onSuccess: invalidate,
      onError,
    }),
    remove: useMutation({ mutationFn: (id: string) => api(`/api/tasks/${id}`, { method: "DELETE" }), onSuccess: invalidate, onError }),
  };
}

export function useProjectMutations() {
  const invalidate = useInvalidateWork();
  const onError = (e: Error) => toast.error(e.message);
  return {
    create: useMutation({ mutationFn: (body: Partial<Project>) => api<{ data: Project }>("/api/projects", { method: "POST", json: body }), onSuccess: invalidate, onError }),
    update: useMutation({ mutationFn: ({ id, ...body }: Partial<Project> & { id: string }) => api(`/api/projects/${id}`, { method: "PATCH", json: body }), onSuccess: invalidate, onError }),
    remove: useMutation({ mutationFn: ({ id, withTasks }: { id: string; withTasks: boolean }) => api(`/api/projects/${id}?withTasks=${withTasks}`, { method: "DELETE" }), onSuccess: invalidate, onError }),
  };
}

export const useCalendar = (from: string, to: string) =>
  useQuery({
    queryKey: ["calendar", from, to],
    queryFn: async () => (await api<{ data: { events: CalendarOccurrence[]; tasks: CalendarTask[] } }>(`/api/calendar?from=${from}&to=${to}`)).data,
  });

export type EventBody = Partial<Omit<CalendarOccurrence, "occurrenceStart" | "occurrenceEnd" | "isRecurring">>;
type Conflict = { id: string; title: string; start: string; end: string };

export function useEventMutations() {
  const invalidate = useInvalidateWork();
  const onError = (e: Error) => toast.error(e.message);
  const warn = (r: { conflicts?: Conflict[] }) => {
    if (r.conflicts?.length) toast.warning(t("Conflit avec : {0}", r.conflicts.map((c) => c.title).join(", ")));
  };
  return {
    create: useMutation({
      mutationFn: (body: EventBody) => api<{ data: CalendarOccurrence; conflicts: Conflict[] }>("/api/calendar", { method: "POST", json: body }),
      onSuccess: (r) => {
        warn(r);
        return invalidate();
      },
      onError,
    }),
    update: useMutation({
      mutationFn: ({ id, ...body }: EventBody & { id: string }) => api<{ data: CalendarOccurrence; conflicts: Conflict[] }>(`/api/calendar/${id}`, { method: "PATCH", json: body }),
      onSuccess: (r) => {
        warn(r);
        return invalidate();
      },
      onError,
    }),
    remove: useMutation({ mutationFn: (id: string) => api(`/api/calendar/${id}`, { method: "DELETE" }), onSuccess: invalidate, onError }),
  };
}

export const useDayPlan = (offset: number, mode: string, breaks: boolean, enabled = true) =>
  useQuery({
    queryKey: ["planning", "day", offset, mode, breaks],
    queryFn: async () => (await api<{ data: DayPlan }>(`/api/planning/day?offset=${offset}&mode=${mode}&breaks=${breaks}`)).data,
    enabled,
  });

export type WeekLoad = { days: { day: string; freeMinutes: number; dueTasks: number; dueMinutes: number }[]; capacityMinutes: number; demandMinutes: number; overload: boolean; ratio: number | null };
export const useWeekLoad = () => useQuery({ queryKey: ["planning", "week"], queryFn: async () => (await api<{ data: WeekLoad }>("/api/planning/week-load")).data });
